import { Component, Input, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { FeatureFlag } from '@app/enums';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { CompanyAdditionalIncome } from '@models/payroll/company-additional-income.model';
import { TIME_WORKED, TimeOffType } from '@models/time-off-v3/time-off-type.model';

@Component({
    selector: 'app-payroll-integration-selection-card',
    templateUrl: './payroll-integration.template.html',
    styleUrls: ['./payroll-integration.style.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class PayrollIntegrationSelectionCard implements OnInit {
    @Input() timeOffType: TimeOffType;
    hasQcPayrollFeature = false;

    allIncomeTypes: { [key: number]: CompanyAdditionalIncome } = {};
    unpaidIncomeTypes: CompanyAdditionalIncome[] = [];
    paidIncomeTypes: CompanyAdditionalIncome[] = [];
    selectableIncomeTypes: CompanyAdditionalIncome[] = [];
    selectedIncomeType?: CompanyAdditionalIncome;

    payrollIntegrationNotice: TranslatableKey = 'time-off-v3.type-store-form.companyHasNoPayrollMessage';
    companyPayrollSyncEnabled = false;
    incomeTypeFieldDisabled = true;
    timeWorkedValue = TIME_WORKED;

    isLoading = true;

    private _isPaid?: boolean | string;
    @Input() set isPaid(value: boolean | string) {
        // In the case of loading edit, we don't want to null out a good incomeTypeId
        // since set isPaid is called before ngOnInit has a chance to run
        if (this._isPaid !== undefined) {
            this.timeOffType.incomeTypeId = null;
        }

        this._isPaid = value;
        this.configureFields();
        this.refreshSelectedIncomeType();
    }

    get isPaid(): boolean | string | undefined {
        return this._isPaid;
    }

    constructor(
        private readonly auth: AuthService,
        private readonly features: FeatureService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            this.features.has(FeatureFlag.qcPayroll).then((hasQcPayrollFeature) => {
                this.hasQcPayrollFeature = hasQcPayrollFeature;
            });

            await this.loadIncomeTypes();

            if (this.timeOffType.isPersisted) {
                this.refreshSelectedIncomeType();
            }
        } catch (e) {
            // if payroll sync is not enabled, an Error is returned by the API and thrown by the code.
            // Empty catch to prevent JS error and we also don't want to show this as a toast.
        } finally {
            this.isLoading = false;
        }
    }

    refreshSelectedIncomeType(): void {
        this.selectedIncomeType = this.allIncomeTypes[this.timeOffType.incomeTypeId] ?? null;
    }

    private configureFields(): void {
        this.selectableIncomeTypes = this.getSelectableIncomeTypes();
        this.payrollIntegrationNotice = this.getPayrollIntegrationNotice();
    }

    private async loadIncomeTypes(): Promise<void> {
        const [incomeTypes] = await CompanyAdditionalIncome.param('company', this.auth.company.id)
            .with(['t4_boxes', 'rl1_boxes'])
            .where('active', true)
            .where('time_off', true)
            .all();

        incomeTypes.forEach((incomeType: CompanyAdditionalIncome) => {
            this.allIncomeTypes[incomeType.id] = incomeType;

            if (incomeType.name === 'unpaid_time_off_salaried') {
                this.unpaidIncomeTypes.push(incomeType);
                return;
            }

            this.paidIncomeTypes.push(incomeType);
        });
    }

    private getSelectableIncomeTypes(): CompanyAdditionalIncome[] {
        if (!this.auth.company.isPayrollSyncEnabled) {
            this.incomeTypeFieldDisabled = true;
            return [];
        }

        switch (this.isPaid) {
            case true:
                this.incomeTypeFieldDisabled = false;
                return this.paidIncomeTypes;
            case false:
                this.incomeTypeFieldDisabled = false;
                return this.unpaidIncomeTypes;
            case TIME_WORKED:
                this.incomeTypeFieldDisabled = true;
                return [];
            default:
                return [];
        }
    }

    private getPayrollIntegrationNotice(): TranslatableKey {
        if (this.auth.company.isSelfServeWithPayroll) {
            return 'time-off-v3.type-store-form.connectToAPayrollIncomeTypeSelfServe';
        }

        if (!this.auth.company.isPayrollSyncEnabled) {
            return 'time-off-v3.type-store-form.companyHasNoPayrollMessage';
        }

        if (this.isPaid === undefined) {
            return 'time-off-v3.type-store-form.connectTimeOffTypeWithAPayrollIncomeType';
        }

        if (this.isPaid === TIME_WORKED) {
            return 'time-off-v3.type-store-form.hoursUnderTimeWorkedWillNotSync';
        }

        return 'time-off-v3.type-store-form.approvedHoursWillBeSynced';
    }
}
